import { useEffect, useState } from 'react';
import type * as React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SortableListProps<T> {
  items: T[];
  itemKey: (item: T) => string;
  onReorder: (ids: string[]) => void;
  renderItem: (item: T) => React.ReactNode;
  disabled?: boolean;
}

/**
 * Drag-reorder for the ordered master-data collections (06 §2).
 *
 * The whole id list is handed back on drop, matching `ReorderRequestDto` — the
 * API takes the full order rather than a moved pair, because a partial reorder
 * has to be applied against a list the admin may not have been looking at.
 *
 * Keyboard sensor included: reordering with a mouse only would make this the
 * one operation in the panel a keyboard user cannot perform.
 */
export function SortableList<T>({
  items,
  itemKey,
  onReorder,
  renderItem,
  disabled = false,
}: SortableListProps<T>): React.JSX.Element {
  // Local order so the row moves under the cursor immediately; the server round
  // trip only confirms it. On failure the caller refetches and this resyncs.
  const [order, setOrder] = useState(() => items.map(itemKey));

  useEffect(() => {
    setOrder(items.map(itemKey));
  }, [items, itemKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byKey = new Map(items.map((item) => [itemKey(item), item] as const));
  const ordered = order
    .map((key) => byKey.get(key))
    .filter((item): item is T => item !== undefined);

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from < 0 || to < 0) return;

    const next = arrayMove(order, from, to);
    setOrder(next);
    onReorder(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-line">
          {ordered.map((item) => (
            <SortableRow key={itemKey(item)} id={itemKey(item)} disabled={disabled}>
              {renderItem(item)}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-3 bg-surface px-4 py-3 transition-colors',
        isDragging ? 'relative z-10 shadow-lg' : 'hover:bg-surface-sunken/50',
      )}
    >
      <button
        type="button"
        aria-label="Reorder"
        disabled={disabled}
        className="cursor-grab rounded p-1 text-ink-faint transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-40"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" aria-hidden="true" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}
